import { useState } from 'react';
import { Download, File, FileText, Film, Music } from 'lucide-react';
import { type ChatAttachmentRef } from '@/lib/api';
import { useFileUrl } from '@/lib/hooks/useFileUrl';
import { cn } from '@/lib/utils';
import { FileViewerModal } from './FileViewerModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileTypeIcon({ mimeType, className }: { mimeType: string; className?: string }) {
  if (mimeType.startsWith('video/')) return <Film className={className} />;
  if (mimeType.startsWith('audio/')) return <Music className={className} />;
  if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text'))
    return <FileText className={className} />;
  return <File className={className} />;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AttachmentItemProps {
  attachment: ChatAttachmentRef;
  /** True when shown inside the sender's own message bubble (green tint). */
  isOwnMessage?: boolean;
}

export function AttachmentItem({ attachment, isOwnMessage }: AttachmentItemProps) {
  const url = useFileUrl(attachment.fileId);
  const [hovered, setHovered] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);

  const isImage = attachment.mimeType.startsWith('image/');
  const isVideo = attachment.mimeType.startsWith('video/');
  const isViewable = isImage || isVideo;

  const handleClick = () => {
    if (!url) return;
    if (isViewable) {
      setViewerOpen(true);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.fileName;
    a.rel = 'noopener noreferrer';
    a.click();
  };

  return (
    <>
      <div className="mt-1.5">
        {/* Filename label */}
        <p
          className={cn(
            'text-[11px] mb-0.5 truncate max-w-[220px]',
            isOwnMessage ? 'text-black/50' : 'text-foreground/50',
          )}
        >
          {attachment.fileName}
        </p>

        {/* Preview area */}
        <div
          className="relative inline-block cursor-pointer rounded-lg overflow-hidden"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={handleClick}
          title={isViewable ? 'Click to view' : 'Click to open in new tab'}
        >
          {isImage && url ? (
            <img
              src={url}
              alt={attachment.fileName}
              className="max-w-[220px] max-h-[160px] object-cover rounded-lg block"
              draggable={false}
            />
          ) : isVideo && url ? (
            /* Video element with preload=metadata shows the first frame as thumbnail */
            <video
              src={url}
              preload="metadata"
              className="max-w-[220px] max-h-[160px] object-cover rounded-lg block"
              muted
            />
          ) : (
            /* Generic file chip */
            <div
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg border min-w-[160px]',
                isOwnMessage ? 'bg-black/10 border-black/15' : 'bg-black/[0.05] border-black/10',
              )}
            >
              <FileTypeIcon
                mimeType={attachment.mimeType}
                className="h-5 w-5 flex-shrink-0 opacity-60"
              />
              <div className="min-w-0">
                <p className="text-[12px] font-medium truncate max-w-[140px]">
                  {attachment.fileName}
                </p>
                <p className="text-[11px] opacity-50">{formatBytes(attachment.sizeBytes)}</p>
              </div>
            </div>
          )}

          {/* Download overlay — appears on hover */}
          {url && hovered && (
            <button
              type="button"
              onClick={handleDownload}
              title="Download"
              className={cn(
                'absolute bottom-1.5 right-1.5 p-1 rounded-full transition-opacity',
                'bg-black/50 text-white hover:bg-black/70',
              )}
            >
              <Download className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Loading shimmer while URL resolves */}
          {!url && (isImage || isVideo) && (
            <div className="w-[180px] h-[120px] rounded-lg bg-black/10 animate-pulse" />
          )}
        </div>
      </div>

      {/* Full-screen viewer (images + videos only) */}
      {viewerOpen && url && (
        <FileViewerModal attachment={attachment} url={url} onClose={() => setViewerOpen(false)} />
      )}
    </>
  );
}
